/**
 * Task-detail data + approve action (T-041 / T-042).
 *
 * The approve action (T-042) has three effects in the AC:
 *   1. status delivered -> approved, approved_at = now()
 *   2. next backlog item (ordered) -> ready_to_pull
 *   3. notification 'task_approved' for Eric; surfaces in /eric Today
 *   4. same action exposed as an MCP tool
 *
 * Effects 2-4 are operator-domain writes. The subscriber RLS only permits
 * effect 1 (tasks_subscriber_update: delivered -> approved on the OWN row;
 * it cannot flip another row to ready_to_pull, and notifications has no
 * subscriber INSERT policy). The clean home for 2-4 + MCP parity (PR-55) is a
 * server endpoint POST /api/tasks/:id/approve — which is server-domain and
 * OUTSIDE Phase G file ownership (server/ + api/ are consume-only).
 *
 * Strategy: try the server endpoint first (it can do all four atomically and
 * is the MCP-parity surface). If it is not present (404) or unreachable, fall
 * back to the subscriber-permitted direct write (effect 1 only) so the
 * subscriber-facing behavior is not blocked on a not-yet-built endpoint. The
 * slot-promotion + Eric-notification + MCP-parity portion is then flagged as
 * a server-side follow-up (documented in IMPL-NOTES for T-042, surfaced to
 * the validator). This keeps Phase G strictly inside its ownership while
 * delivering the subscriber action today.
 */

import { supabase } from '../supabase.js';

export type ApproveResult =
  | { ok: true; via: 'endpoint' | 'direct' }
  | { ok: false; message: string };

export async function approveTask(
  taskId: string,
  accessToken: string,
): Promise<ApproveResult> {
  // 1. Preferred path: the server endpoint (atomic, MCP-parity surface).
  try {
    const res = await fetch(
      `/api/tasks/${encodeURIComponent(taskId)}/approve`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    if (res.status === 200) return { ok: true, via: 'endpoint' };
    // 404 / 405 => endpoint not built yet; fall through to the direct write.
    if (res.status !== 404 && res.status !== 405) {
      return {
        ok: false,
        message:
          'Could not approve this deliverable. Try again in a moment.',
      };
    }
  } catch {
    // Network error reaching the endpoint; fall through to the direct write.
  }

  // 2. Fallback: subscriber-permitted direct write (effect 1 only). RLS
  //    tasks_subscriber_update permits delivered -> approved on the own row.
  const { error } = await supabase
    .from('tasks')
    .update({ status: 'approved', approved_at: new Date().toISOString() })
    .eq('id', taskId)
    .eq('status', 'delivered');
  if (error) {
    return {
      ok: false,
      message: 'Could not approve this deliverable. Try again in a moment.',
    };
  }
  return { ok: true, via: 'direct' };
}
